import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKAGES = [
  {
    id: "trial",
    label: "7-Day Trial",
    price: "$0.99",
    period: "one-time",
    description: "Try every Premium feature for 7 full days. One per athlete.",
    accent: "border-purple-500/60",
    accentText: "text-purple-400",
    cta: "Start Trial",
    badge: "BEST FIRST STEP",
  },
  {
    id: "monthly",
    label: "Monthly",
    price: "$18.99",
    period: "for 30 days",
    description: "Premium access for one full month. No auto-renew, pay again when you're ready.",
    accent: "border-[#D4AF37]",
    accentText: "text-[#D4AF37]",
    cta: "Go Monthly",
    badge: "MOST POPULAR",
    featured: true,
  },
  {
    id: "yearly",
    label: "Yearly",
    price: "$189",
    period: "for 365 days",
    description: "A full year of Premium. Saves ~$39 vs paying monthly — almost 2 months free.",
    accent: "border-red-700/70",
    accentText: "text-red-400",
    cta: "Go Yearly",
    badge: "BEST VALUE",
  },
];

const PERKS = [
  "Full AI Coach access — unlimited plan generations",
  "Macro Master food log with barcode + history",
  "Unlimited 🥚 Egg Spin Wheel + Phoenix Hatch boosts",
  "Unlimited 📸 Food Photo AI scans (free is 3/day)",
  "Unlock multi-tab Shopping List 🛒 + recipe auto-import",
  "Chief Cook David Lauer egg 👨‍🍳 — chance of a 24h VIP Pass on hatch",
  "Muscle Tracker advanced analytics",
  "Inner Circle premium badge 🥇 + Public Profile boost",
  "+1000 Phoenix Token welcome bonus 🐦‍🔥",
  "Owner's Perch exclusive content + Hall of Fame priority",
];;

export default function Plans() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busyId, setBusyId] = useState(null);

  async function startCheckout(pkgId) {
    if (!user) {
      toast.error("Sign in or create a free account first.");
      navigate("/?signin=1");
      return;
    }
    if (pkgId === "trial" && user.trial_used) {
      toast.error("You've already used your 7-day trial. Pick a paid plan.");
      return;
    }
    setBusyId(pkgId);
    try {
      const { data } = await axios.post(
        `${API}/billing/checkout`,
        { package_id: pkgId, origin_url: window.location.origin },
        { withCredentials: true }
      );
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
      setBusyId(null);
    }
  }

  return (
    <div className="bg-[#0A0A0A] min-h-screen text-white" data-testid="plans-page">
      <section className="max-w-6xl mx-auto px-4 py-16 sm:py-24">
        <div className="text-center mb-12">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-2">
            OmniMuscle Premium
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white mb-4">
            Unlock the <span className="text-[#D4AF37]">Full Phoenix.</span>
          </h1>
          <p className="text-base sm:text-lg text-neutral-400 max-w-2xl mx-auto">
            Holistic healing tools, AI coaching, deep analytics, and the Inner Circle —
            three ways to fly higher.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {PACKAGES.map((p) => (
            <div
              key={p.id}
              data-testid={`plan-${p.id}`}
              className={`relative bg-[#0F0F0F] border-2 ${p.accent} p-8 transition-all ${
                p.featured ? "md:scale-105 shadow-2xl shadow-[#D4AF37]/10" : "hover:border-white"
              }`}
            >
              {p.badge && (
                <div
                  className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-[9px] font-bold uppercase tracking-[0.2em] bg-[#0A0A0A] border ${p.accent} ${p.accentText}`}
                >
                  {p.badge}
                </div>
              )}
              <div className={`text-[10px] uppercase tracking-[0.3em] ${p.accentText} font-bold mb-2`}>
                {p.label}
              </div>
              <div className="flex items-baseline gap-2 mb-4">
                <span className="font-display text-5xl text-white">{p.price}</span>
                <span className="text-sm text-neutral-500">{p.period}</span>
              </div>
              <p className="text-sm text-neutral-400 mb-6 min-h-[60px]">{p.description}</p>
              <button
                data-testid={`plan-${p.id}-buy`}
                onClick={() => startCheckout(p.id)}
                disabled={busyId === p.id || (p.id === "trial" && user?.trial_used)}
                className={`w-full font-bold uppercase tracking-[0.2em] text-sm py-4 transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                  p.featured
                    ? "bg-[#D4AF37] hover:bg-[#F1C84B] text-[#0A0A0A]"
                    : "bg-white hover:bg-neutral-200 text-[#0A0A0A]"
                }`}
              >
                {busyId === p.id
                  ? "Redirecting..."
                  : p.id === "trial" && user?.trial_used
                  ? "Trial Used"
                  : p.cta}
              </button>
            </div>
          ))}
        </div>

        <div className="bg-[#0F0F0F] border border-zinc-900 p-8 sm:p-10">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-2">
            What you unlock
          </div>
          <h2 className="font-display text-2xl sm:text-3xl text-white mb-6">
            Every tier — every perk.
          </h2>
          <ul className="grid sm:grid-cols-2 gap-3">
            {PERKS.map((perk) => (
              <li key={perk} className="flex items-start gap-3 text-sm text-neutral-300">
                <span className="text-[#D4AF37] mt-0.5 font-bold">✓</span>
                <span>{perk}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="text-[10px] text-neutral-600 uppercase tracking-widest text-center mt-8">
          Secure checkout by Stripe · No auto-renew · Cancel anytime in profile
        </p>
      </section>
    </div>
  );
}
